// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Clock, ArrowLeft, Share2, BookOpen, Calculator, ChevronDown, Scale } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_COLORS = {
  'Basi': '#3D7A52', 'Investimenti': '#5B8DB8', 'Casa': '#C9A84C',
  'Lavoro': '#E87040', 'Previdenza': '#8B6FD4', 'Sicurezza': '#E84040',
  'Finanza Personale': '#C9A84C',
};

// Deployment 30 — Backlink automatico articolo giuridico → calcolatore correlato.
// Mapping estendibile: aggiungere qui la coppia slug/calcolatore per abilitarlo.
const RELATED_CALCULATOR = {
  'successione-ereditaria': {
    path: '/calcolatori/imposta-successione',
    title: 'Calcola la tua imposta di successione',
    desc: 'Simula franchigie, aliquote, quote di legittima e imposte ipocatastali sulla tua situazione familiare reale.',
    cta: 'Apri il calcolatore',
    testid: 'blog-backlink-imposta-successione',
  },
  'busta-paga': {
    path: '/calcolatori/lettore-busta-paga',
    title: 'Analizza la tua busta paga',
    desc: 'Verifica netto, contributi INPS, IRPEF trattenuta e TFR accantonato inserendo le tue voci reali in totale anonimato.',
    cta: 'Apri il calcolatore',
    testid: 'blog-backlink-lettore-busta-paga',
  },
  'detrazioni-730': {
    path: '/calcolatori/detrazioni-730',
    title: 'Calcola il tuo risparmio fiscale con il 730',
    desc: 'Simula il rimborso IRPEF applicando spese mediche, interessi mutuo, ristrutturazioni e altre detrazioni al 19% e al 50%.',
    cta: 'Apri il calcolatore',
    testid: 'blog-backlink-detrazioni-730',
  },
  'crypto-tassazione': {
    path: '/calcolatori/crypto-imposte',
    title: 'Calcola le tue imposte crypto 2026',
    desc: 'Applica le nuove aliquote 33%/26% (L. 207/2024 + L. 199/2025) sulle tue plusvalenze crypto, con gestione FIFO e minusvalenze.',
    cta: 'Apri il calcolatore',
    testid: 'blog-backlink-crypto-imposte',
  },
};

// Deployment 30 — Estrazione automatica citazioni giuridiche dal contenuto
// markdown e generazione link a Normattiva (URN diretto).
// Pattern supportati: art./artt. N c.c. (range e -bis), L. N/YYYY, D.L. N/YYYY,
// D.Lgs. N/YYYY, D.P.R. N/YYYY, art. N Cost., art. N TUIR (linka a D.P.R. 917/1986).
const CC_URN = 'https://www.normattiva.it/uri-res/N2Ls?urn:nir:stato:regio.decreto:1942-03-16;262';
const COST_URN = 'https://www.normattiva.it/uri-res/N2Ls?urn:nir:stato:costituzione:1947-12-27';
const TUIR_URN = 'https://www.normattiva.it/uri-res/N2Ls?urn:nir:stato:decreto.del.presidente.della.repubblica:1986-12-22;917';

const extractLegalRefs = (content) => {
  if (!content) return [];
  const refs = [];
  const seen = new Set();

  // Ranges: "artt. 536-542 c.c."
  const rangeRe = /\bartt\.\s*(\d+)-(\d+)\s*c\.c\./g;
  let m;
  while ((m = rangeRe.exec(content)) !== null) {
    const key = `range-${m[1]}-${m[2]}`;
    if (seen.has(key)) continue;
    seen.add(key);
    refs.push({
      label: `Artt. ${m[1]}-${m[2]} c.c.`,
      url: `${CC_URN}~art${m[1]}`,
      kind: 'cc',
    });
  }

  // Singoli c.c. con eventuale suffisso -bis/-ter/... e opzionale "e ss."
  const singleCcRe = /\bart(?:t)?\.\s*(\d+(?:-(?:bis|ter|quater|quinquies|sexies|septies|octies|novies|decies))?)(\s*e\s*ss\.)?\s*c\.c\./g;
  while ((m = singleCcRe.exec(content)) !== null) {
    const num = m[1];
    const eSs = m[2] ? ' e ss.' : '';
    const isInRange = refs.some(r => {
      const rm = r.label.match(/^Artt\. (\d+)-(\d+) c\.c\.$/);
      if (!rm) return false;
      const n = parseInt(num, 10);
      return !isNaN(n) && n >= parseInt(rm[1], 10) && n <= parseInt(rm[2], 10);
    });
    if (isInRange) continue;
    const key = `art-cc-${num}${eSs}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const anchor = num.replace('-', '');
    refs.push({
      label: `Art. ${num}${eSs} c.c.`,
      url: `${CC_URN}~art${anchor}`,
      kind: 'cc',
    });
  }

  // Art. Costituzione: "art. 36 Cost."
  const costRe = /\bart\.\s*(\d+(?:-(?:bis|ter|quater))?)\s*Cost\./g;
  while ((m = costRe.exec(content)) !== null) {
    const num = m[1];
    const key = `art-cost-${num}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const anchor = num.replace('-', '');
    refs.push({
      label: `Art. ${num} Cost.`,
      url: `${COST_URN}~art${anchor}`,
      kind: 'cost',
    });
  }

  // Art. TUIR: "art. 15 c.1 lett. c TUIR", "art. 16-bis TUIR", "art. 67 c.1 lett. c-sexies TUIR"
  // Cattura solo il numero (e suffisso -bis/-ter) — commi e lettere restano nel testo,
  // dedup sull'articolo (art. 15 TUIR = una sola voce, indipendentemente da lett. b/c).
  const tuirArtRe = /\bart\.\s*(\d+(?:-(?:bis|ter|quater|quinquies|sexies|septies|octies|novies|decies))?)(?:\s+c\.\s*\d+(?:\s+lett\.\s*[a-z](?:-(?:bis|ter|quater|quinquies|sexies|septies|octies))?)?)?\s*TUIR/g;
  while ((m = tuirArtRe.exec(content)) !== null) {
    const num = m[1];
    const key = `art-tuir-${num}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const anchor = num.replace('-', '');
    refs.push({
      label: `Art. ${num} TUIR`,
      url: `${TUIR_URN}~art${anchor}`,
      kind: 'tuir',
    });
  }

  // TUIR generico (senza articolo specifico), es. "TUIR (D.P.R. 917/1986)"
  // Solo se non abbiamo già aggiunto una entry TUIR/art.
  const hasTuir = refs.some(r => r.kind === 'tuir' || (r.kind === 'dpr' && r.label.includes('917/1986')));
  if (!hasTuir && /\bTUIR\b/.test(content)) {
    refs.push({
      label: 'TUIR (D.P.R. 917/1986)',
      url: TUIR_URN,
      kind: 'tuir',
    });
    seen.add('tuir-generic');
  }

  // D.P.R. N/YYYY: "D.P.R. 917/1986"
  const dprRe = /\bD\.?P\.?R\.?\s*(\d+)\/(\d{4})\b/g;
  while ((m = dprRe.exec(content)) !== null) {
    const key = `dpr-${m[1]}-${m[2]}`;
    if (seen.has(key)) continue;
    seen.add(key);
    // Se è il 917/1986 (TUIR) e abbiamo già un'entry TUIR, non duplichiamo
    if (m[1] === '917' && m[2] === '1986' && refs.some(r => r.kind === 'tuir')) continue;
    refs.push({
      label: `D.P.R. ${m[1]}/${m[2]}`,
      url: `https://www.normattiva.it/uri-res/N2Ls?urn:nir:stato:decreto.del.presidente.della.repubblica:${m[2]};${m[1]}`,
      kind: 'dpr',
    });
  }

  // D.Lgs. N/YYYY: "D.Lgs. 41/2024"
  const dlgsRe = /\bD\.?Lgs\.?\s*(\d+)\/(\d{4})\b/g;
  while ((m = dlgsRe.exec(content)) !== null) {
    const key = `dlgs-${m[1]}-${m[2]}`;
    if (seen.has(key)) continue;
    seen.add(key);
    refs.push({
      label: `D.Lgs. ${m[1]}/${m[2]}`,
      url: `https://www.normattiva.it/uri-res/N2Ls?urn:nir:stato:decreto.legislativo:${m[2]};${m[1]}`,
      kind: 'dlgs',
    });
  }

  // D.L. N/YYYY: "D.L. 167/1990" (NON confondere con D.Lgs., già gestito sopra;
  // il negative lookahead assicura che non sia "D.Lgs.")
  const dlRe = /\bD\.?L\.?\s*(?!gs)(\d+)\/(\d{4})\b/gi;
  while ((m = dlRe.exec(content)) !== null) {
    // Skip se il testo circostante mostra "D.Lgs." (già coperto)
    const before = content.substring(Math.max(0, m.index - 3), m.index).toLowerCase();
    if (before.includes('lg')) continue;
    const key = `dl-${m[1]}-${m[2]}`;
    if (seen.has(key)) continue;
    seen.add(key);
    refs.push({
      label: `D.L. ${m[1]}/${m[2]}`,
      url: `https://www.normattiva.it/uri-res/N2Ls?urn:nir:stato:decreto.legge:${m[2]};${m[1]}`,
      kind: 'dl',
    });
  }

  // Leggi ordinarie: "L. 55/2006", "L. 80/2005", "L. 232/2016"
  // (Il regex \bL\. matcha "L." ma NON "L.gs" grazie al \b + il fatto che dopo
  // "L." serve uno spazio o cifra: usiamo lookahead per essere sicuri.)
  const lawRe = /\bL\.\s*(\d+)\/(\d{4})\b/g;
  while ((m = lawRe.exec(content)) !== null) {
    // Skip se preceduto da "D." (già D.L. o D.Lgs.)
    const before = content.substring(Math.max(0, m.index - 4), m.index);
    if (/D\.\s*$/.test(before) || /D\.L\.\s*$/i.test(before) || /D\.Lgs\.\s*$/i.test(before)) continue;
    const key = `L-${m[1]}-${m[2]}`;
    if (seen.has(key)) continue;
    seen.add(key);
    refs.push({
      label: `L. ${m[1]}/${m[2]}`,
      url: `https://www.normattiva.it/uri-res/N2Ls?urn:nir:stato:legge:${m[2]};${m[1]}`,
      kind: 'legge',
    });
  }

  return refs;
};

// Simple Markdown renderer (headings, bold, lists, code, tables)
const renderMarkdown = (text) => {
  if (!text) return '';
  const lines = text.split('\n');
  const result = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // H2
    if (line.startsWith('## ')) {
      result.push(`<h2 class="text-xl font-semibold text-[#E8E8E4] mt-8 mb-4">${line.slice(3)}</h2>`);
    }
    // H3
    else if (line.startsWith('### ')) {
      result.push(`<h3 class="text-base font-semibold text-[#E8E8E4] mt-6 mb-3">${line.slice(4)}</h3>`);
    }
    // Horizontal rule
    else if (line.startsWith('---')) {
      result.push('<hr class="border-white/10 my-6" />');
    }
    // Code block
    else if (line.startsWith('```')) {
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      result.push(`<pre class="bg-[#252523] border border-white/10 rounded-lg p-4 text-sm text-[#E8E8E4] overflow-x-auto my-4"><code>${codeLines.join('\n')}</code></pre>`);
    }
    // Table
    else if (line.startsWith('|')) {
      const tableLines = [line];
      i++;
      while (i < lines.length && lines[i].startsWith('|')) {
        tableLines.push(lines[i]);
        i++;
      }
      const rows = tableLines.filter(l => !l.match(/^\|[\s-|]+\|$/));
      const tableHtml = rows.map((row, ri) => {
        const cells = row.split('|').filter(c => c.trim());
        const tag = ri === 0 ? 'th' : 'td';
        const cls = ri === 0
          ? `text-left py-2 px-4 text-xs font-semibold text-[#C9A84C] uppercase bg-[rgba(201,168,76,0.08)]`
          : `py-2.5 px-4 text-sm text-[#888780] border-b border-white/[0.04]`;
        return `<tr>${cells.map(c => `<${tag} class="${cls}">${c.trim().replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#E8E8E4]">$1</strong>')}</${tag}>`).join('')}</tr>`;
      }).join('');
      result.push(`<div class="overflow-x-auto my-6"><table class="w-full border border-white/10 rounded-lg overflow-hidden">${tableHtml}</table></div>`);
      continue;
    }
    // Bullet list
    else if (line.startsWith('- ')) {
      const listLines = [line];
      i++;
      while (i < lines.length && lines[i].startsWith('- ')) {
        listLines.push(lines[i]);
        i++;
      }
      const items = listLines.map(l => {
        const content = l.slice(2).replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#E8E8E4]">$1</strong>').replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-[#C9A84C] hover:underline">$1</a>');
        return `<li class="text-[#888780] leading-relaxed">${content}</li>`;
      }).join('');
      result.push(`<ul class="list-disc list-inside space-y-1.5 my-4 pl-2">${items}</ul>`);
      continue;
    }
    // Numbered list
    else if (/^\d+\. /.test(line)) {
      const listLines = [line];
      i++;
      while (i < lines.length && /^\d+\. /.test(lines[i])) {
        listLines.push(lines[i]);
        i++;
      }
      const items = listLines.map(l => {
        const content = l.replace(/^\d+\. /, '').replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#E8E8E4]">$1</strong>').replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-[#C9A84C] hover:underline">$1</a>');
        return `<li class="text-[#888780] leading-relaxed">${content}</li>`;
      }).join('');
      result.push(`<ol class="list-decimal list-inside space-y-1.5 my-4 pl-2">${items}</ol>`);
      continue;
    }
    // Empty line
    else if (line.trim() === '') {
      result.push('<div class="h-2"></div>');
    }
    // Normal paragraph
    else {
      const processed = line
        .replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#E8E8E4]">$1</strong>')
        .replace(/`([^`]+)`/g, '<code class="bg-[#252523] text-[#C9A84C] px-1.5 py-0.5 rounded text-sm">$1</code>')
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-[#C9A84C] hover:underline">$1</a>');
      result.push(`<p class="text-[#888780] leading-relaxed">${processed}</p>`);
    }
    i++;
  }
  return result.join('\n');
};

const BlogArticlePage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await fetch(`${API_URL}/api/blog/posts/${slug}`);
        if (res.ok) {
          setPost(await res.json());
        } else {
          setNotFound(true);
        }
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1C1C1A]">
        <div className="w-8 h-8 border-2 border-[#C9A84C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen bg-[#1C1C1A] flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-[#E8E8E4] mb-3">Articolo non trovato</h1>
          <Link to="/blog" className="text-[#C9A84C] hover:underline">← Torna al blog</Link>
        </div>
      </div>
    );
  }

  const color = CATEGORY_COLORS[post.category] || '#C9A84C';
  const dateStr = post.published_at ? new Date(post.published_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: post.title, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <div className="min-h-screen bg-[#1C1C1A]">
      <Helmet>
        <title>{`${post.title} — SAPIENS`}</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={`https://finsapiens.it/blog/${post.slug}`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": post.title,
          "description": post.excerpt,
          "url": `https://finsapiens.it/blog/${post.slug}`,
          "datePublished": post.date || post.created_at,
          "dateModified": post.updated_at || post.date,
          "publisher": {
            "@type": "Organization",
            "name": "SAPIENS",
            "url": "https://finsapiens.it",
            "logo": { "@type": "ImageObject", "url": "https://finsapiens.it/logo.png" }
          },
          "author": { "@type": "Organization", "name": "Redazione SAPIENS" },
          "mainEntityOfPage": { "@type": "WebPage", "@id": `https://finsapiens.it/blog/${post.slug}` }
        })}</script>
      </Helmet>

      <div className="max-w-3xl mx-auto px-4 py-10">
        {/* Back */}
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-[#888780] hover:text-[#C9A84C] transition-colors mb-8" data-testid="blog-article-back">
          <ArrowLeft className="w-4 h-4" />
          Torna al blog
        </Link>

        <motion.article initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Category + meta */}
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: `${color}15`, color }}>
              {post.category}
            </span>
            <span className="text-xs text-[#555553]">{dateStr}</span>
            <span className="flex items-center gap-1 text-xs text-[#555553]">
              <Clock className="w-3 h-3" />{post.read_time} min
            </span>
          </div>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl font-semibold text-[#E8E8E4] leading-tight mb-4" data-testid="blog-article-title">
            {post.title}
          </h1>

          {/* Excerpt */}
          <p className="text-lg text-[#888780] leading-relaxed mb-8 pb-8 border-b border-white/10">
            {post.excerpt}
          </p>

          {/* Content */}
          <div
            className="prose-custom"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(post.content) }}
          />

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-[#555553]">
              <BookOpen className="w-4 h-4" />
              {post.author}
            </div>
            <button
              onClick={handleShare}
              className="flex items-center gap-2 text-sm text-[#888780] hover:text-[#C9A84C] transition-colors"
              data-testid="blog-article-share"
            >
              <Share2 className="w-4 h-4" />
              Condividi
            </button>
          </div>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6">
              {post.tags.map(tag => (
                <span key={tag} className="px-3 py-1 rounded-full text-xs text-[#555553] border border-white/10" style={{ background: '#252523' }}>
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* MiFID II Inline Disclaimer — category-based (Deployment 12) */}
          {(() => {
            const cat = (post.category || '').toLowerCase();
            const slug = (post.slug || '').toLowerCase();
            const isFiscal = cat === 'fiscalità' || cat === 'fiscalita' || /(crypto|tassazione|730|partita-iva|forfettario)/.test(slug);
            const isInvestments = cat === 'investimenti' || cat === 'previdenza';
            if (!isFiscal && !isInvestments) return null;
            return (
              <div className="mt-8 p-4 rounded-xl text-xs text-[#888780]" style={{ background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.15)' }} data-testid={isFiscal ? 'mifid-fiscal-disclaimer' : 'mifid-investment-disclaimer'}>
                <span className="font-semibold text-[#C9A84C]">{isFiscal ? 'Avvertenza fiscale: ' : 'Avvertenza MiFID II: '}</span>
                {isFiscal
                  ? "I contenuti hanno scopo esclusivamente educativo e non costituiscono consulenza fiscale ai sensi del D.Lgs. 28/2010 e della normativa tributaria vigente. Le aliquote e le regole indicate possono cambiare in funzione di variazioni legislative. Per la tua situazione specifica rivolgiti a un commercialista o professionista fiscale abilitato."
                  : "I contenuti di questo articolo hanno esclusivamente scopo educativo e informativo. Non costituiscono consulenza in materia di investimenti ai sensi della Direttiva 2014/65/UE (MiFID II) né raccomandazione personalizzata su strumenti finanziari. I rendimenti storici non garantiscono risultati futuri. Per decisioni di investimento rivolgiti a un consulente finanziario indipendente iscritto all'OCF."}
              </div>
            );
          })()}

          {/* CTA */}
          {(() => {
            const rel = RELATED_CALCULATOR[post.slug];
            const legalRefs = extractLegalRefs(post.content);
            return (
              <>
                {/* Fonti normative collassabili (Deployment 30) — solo se il testo cita almeno una fonte */}
                {legalRefs.length > 0 && (
                  <details
                    className="mt-8 rounded-2xl overflow-hidden group"
                    style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.08)' }}
                    data-testid="blog-fonti-normative"
                  >
                    <summary
                      className="flex items-center justify-between gap-3 px-5 py-4 cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden"
                      data-testid="blog-fonti-normative-toggle"
                    >
                      <div className="flex items-center gap-3">
                        <Scale className="w-4 h-4 text-[#C9A84C]" />
                        <span className="text-sm font-semibold text-[#E8E8E4]">Fonti normative</span>
                        <span className="text-xs text-[#555553]">({legalRefs.length})</span>
                      </div>
                      <ChevronDown className="w-4 h-4 text-[#888780] transition-transform group-open:rotate-180" />
                    </summary>
                    <div className="px-5 pb-5 pt-1">
                      <p className="text-xs text-[#888780] mb-3">
                        Riferimenti normativi citati in questo articolo, con collegamento diretto al testo ufficiale su Normattiva.
                      </p>
                      <ul className="space-y-1.5">
                        {legalRefs.map((ref, idx) => (
                          <li key={idx} className="text-sm">
                            <a
                              href={ref.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-[#C9A84C] hover:underline"
                              data-testid={`blog-fonte-normativa-${idx}`}
                            >
                              {ref.label}
                              <span className="text-[10px] opacity-60">↗ Normattiva</span>
                            </a>
                          </li>
                        ))}
                      </ul>
                      <p className="text-[10px] text-[#555553] mt-4 leading-relaxed">
                        Fonte: Normattiva — portale ufficiale della normativa vigente della Repubblica Italiana.
                        In caso di anchor non risolto, il link porta al testo integrale del Codice Civile
                        (R.D. 16 marzo 1942, n. 262) o della legge citata.
                      </p>
                    </div>
                  </details>
                )}

                {/* Backlink calcolatore correlato (Deployment 30) — targeted CTA */}
                {rel && (
                  <div
                    className="mt-6 p-6 rounded-2xl flex flex-col sm:flex-row sm:items-center gap-4"
                    style={{ background: 'rgba(93,141,184,0.08)', border: '1px solid rgba(93,141,184,0.28)' }}
                    data-testid={rel.testid}
                  >
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                      style={{ background: 'rgba(93,141,184,0.18)' }}
                    >
                      <Calculator className="w-5 h-5 text-[#5B8DB8]" />
                    </div>
                    <div className="flex-1">
                      <div className="text-[#E8E8E4] font-semibold mb-1">{rel.title}</div>
                      <p className="text-sm text-[#888780] leading-relaxed">{rel.desc}</p>
                    </div>
                    <Link
                      to={rel.path}
                      className="sapiens-btn-primary px-5 py-2.5 text-sm text-center shrink-0"
                      data-testid={`${rel.testid}-cta`}
                    >
                      {rel.cta} →
                    </Link>
                  </div>
                )}

                {/* CTA generico esistente */}
                <div className="mt-6 p-6 rounded-2xl text-center" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)' }}>
                  <div className="text-[#C9A84C] font-semibold mb-2">Metti in pratica quello che hai letto</div>
                  <p className="text-sm text-[#888780] mb-4">Usa i calcolatori SAPIENS per applicare questi concetti alla tua situazione reale.</p>
                  <Link to="/calcolatori" className="sapiens-btn-primary px-6 py-2.5 inline-block text-sm" data-testid="blog-article-cta">
                    Esplora i calcolatori
                  </Link>
                </div>
              </>
            );
          })()}
        </motion.article>
      </div>
    </div>
  );
};

export default BlogArticlePage;
